import { getCustomerFacingPriceItem } from '@/helpers/customerPricing/customerPricing'
import { mobileMediaQuery } from '@caterdesk/ui--theme'
import { TextExtraSmall } from '@caterdesk/ui--typography'
import { amountToPriceString, Currency } from '@caterdesk/utils--money'
import { DietaryRow } from '@fusion/dietary/row'
import { Box, Checkbox, FormControlLabel, Stack } from '@mui/material'

import React from 'react'
import { useMedia } from 'react-use'

import type { PriceAudience } from '../../domain/menu-item'
import { MenuItemOption, MenuOptionItem } from '../../domain/menu-item-option'
import { SelectedOptionItem } from './menu-item-modal'

type Props = {
  option: MenuItemOption
  currency: Currency
  selectedOptionItems?: Map<string, SelectedOptionItem>
  selectOptionItem: (option: MenuItemOption, item: MenuOptionItem) => void
  pricesIncTax?: boolean
  priceAudience?: PriceAudience
  showPriceBasisPerItem?: boolean
  taxAcronym: string
}

export const TickOptions: React.FC<Props> = ({
  option,
  currency,
  selectedOptionItems,
  selectOptionItem,
  pricesIncTax,
  priceAudience,
  showPriceBasisPerItem,
  taxAcronym,
}) => {
  const isMobileScreen = useMedia(mobileMediaQuery, false)

  return (
    <Box
      sx={{
        py: isMobileScreen ? 1 : 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-of-type': { borderBottom: 'none' },
      }}>
      <Stack direction="column" alignItems="stretch" spacing={1}>
        {option.items.map((item) => {
          const priceSource =
            priceAudience === 'vendor'
              ? { price: item.vendorPrice, priceExTax: item.vendorPriceExTax }
              : getCustomerFacingPriceItem(item.customerPriceItems)
          const isIncTaxFallback = !pricesIncTax && priceSource.priceExTax == null
          const priceToShow = pricesIncTax
            ? priceSource.price
            : (priceSource.priceExTax ?? priceSource.price)
          return (
            <Box key={item._id} sx={{ px: isMobileScreen ? 2 : 3 }}>
              <Stack spacing={0.5}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <FormControlLabel
                    key={item?._id}
                    label=""
                    disabled={
                      !selectedOptionItems?.has(item._id) &&
                      selectedOptionItems?.size === option.maxQty
                    }
                    control={
                      <Checkbox
                        size="small"
                        checked={selectedOptionItems ? selectedOptionItems.has(item._id) : false}
                        onChange={() => selectOptionItem(option, item)}
                        name={option.name || 'option'}
                      />
                    }
                    sx={{ margin: 0 }}
                  />
                  <TextExtraSmall sx={{ fontWeight: 500, flex: 1 }}>
                    {item.item.name}
                  </TextExtraSmall>
                  {typeof priceToShow === 'number' && (
                    <TextExtraSmall>
                      {amountToPriceString(currency, priceToShow, true)}
                      {showPriceBasisPerItem &&
                        ` (${isIncTaxFallback || pricesIncTax ? 'inc' : 'ex'} ${taxAcronym})`}
                    </TextExtraSmall>
                  )}
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ pl: 5 }}>
                  <DietaryRow dietaries={item.item.dietaries} allergens={[]} maxToShow={3} />
                  <DietaryRow
                    maxToShow={3}
                    dietaries={[]}
                    allergens={item.item.allergens}
                    subAllergens={item.item.subAllergens}
                  />
                </Stack>
              </Stack>
            </Box>
          )
        })}
      </Stack>
    </Box>
  )
}
